/**
 * The store stocks imported from Cegid, searched and sorted by the API and
 * loaded page by page, with the stock integration switch. Admin only.
 */
import { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch, faRedo } from '@fortawesome/free-solid-svg-icons';
import { ROLES } from '@/shared/constants';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { SearchForm } from '@/shared/ui/SearchForm';
import { SortableHeader } from '@/shared/ui/SortableHeader';
import { lastApiCallQueryOptions, stockPagesQueryOptions } from './api';
import {
  formatStockDate,
  nextStoreSort,
  type StoreSort,
  type StoreSortField,
} from './logic';
import { StockToggle } from './StockToggle';
import { kebabCase } from '@/shared/lib/kebab-case';

const COLUMNS: { field: StoreSortField; label: string; width: string }[] = [
  { field: 'storeName', label: 'Store', width: '17%' },
  { field: 'storeCode', label: 'Store Code', width: '13%' },
  { field: 'ean', label: 'EAN', width: '20%' },
  { field: 'sku', label: 'Product M3SKU (stock)', width: '20%' },
  { field: 'quantity', label: 'Quantity', width: '15%' },
  { field: 'lastImportId', label: 'Last updated date', width: '15%' },
];

export function StocksPage() {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;

  useEffect(() => {
    if (user && !isAdmin) hardNavigate('/main');
  }, [user, isAdmin]);

  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sort, setSort] = useState<StoreSort>({
    by: 'storeCode',
    ascending: true,
  });
  const [refresh, setRefresh] = useState(0);

  const stocks = useInfiniteQuery({
    ...stockPagesQueryOptions({
      search: appliedSearch,
      sortBy: sort.by,
      sortOrder: sort.ascending ? 'asc' : 'desc',
      refresh,
    }),
    enabled: isAdmin,
  });
  const lastApiCall = useQuery({
    ...lastApiCallQueryOptions('stock', refresh),
    enabled: isAdmin,
  });
  const items = stocks.data?.pages.flatMap((page) => page.data) ?? [];
  const nextCursor = stocks.data?.pages.at(-1)?.nextCursor ?? null;
  const isLoading = stocks.isPending;

  if (!isAdmin) return null;

  return (
    <div className="container summary">
      <TopMenu />
      <div className="summary-table mb-12 mt-6">
        <div className="mb-4 inline-flex items-center justify-end">
          <StockToggle configType="stocksActive" label="Stock" />
          <button
            type="button"
            className="btn btn-dark btn-circle btn-icon"
            aria-label="Refresh"
            disabled={isLoading}
            onClick={() => setRefresh((count) => count + 1)}
            data-testid="stock-refresh-button"
          >
            <FontAwesomeIcon icon={faRedo} spin={isLoading} />
          </button>
        </div>
        <div className="mb-4 flex items-center justify-between">
          <SearchForm
            value={search}
            onChange={setSearch}
            onSubmit={() => setAppliedSearch(search)}
            testId="stock-search"
          />
          <div
            className="border px-6 py-1"
            data-testid="header-stock-last-update"
          >
            Last updated date:{' '}
            {lastApiCall.data
              ? formatStockDate(lastApiCall.data.lastUpdate)
              : '-'}
          </div>
        </div>
        <div className="mb-6">
          <div className="table-container">
            <div className="items-container" role="region" aria-label="Stocks">
              <table className="table items">
                <thead>
                  <tr>
                    {COLUMNS.map(({ field, label, width }) => (
                      <SortableHeader
                        key={field}
                        label={label}
                        width={width}
                        active={sort.by === field}
                        descending={!sort.ascending}
                        arrow={sort.ascending ? 'up' : 'down'}
                        onClick={() =>
                          setSort((current) => nextStoreSort(current, field))
                        }
                        testId={`stock-header-${kebabCase(field)}`}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center">
                        <FontAwesomeIcon
                          icon={faCircleNotch}
                          spin
                          size="2x"
                          className="text-muted"
                        />
                        <div className="text-muted mt-2">Loading items...</div>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {items.map((stock, index) => (
                        <tr key={stock._id ?? index} className="stock-row">
                          <td
                            style={{ width: '20%' }}
                            title={stock.storeName ?? ''}
                            data-testid="stock-row-store-name"
                          >
                            {stock.storeName}
                          </td>
                          <td
                            style={{ width: '10%' }}
                            title={String(stock.storeCode ?? '')}
                            data-testid="stock-row-store-code"
                          >
                            {stock.storeCode}
                          </td>
                          <td
                            style={{ width: '20%' }}
                            title={stock.ean ?? ''}
                            data-testid="stock-row-ean"
                          >
                            {stock.ean}
                          </td>
                          <td
                            style={{ width: '20%' }}
                            title={stock.sku ?? ''}
                            data-testid="stock-row-m3sku"
                          >
                            {stock.sku}
                          </td>
                          <td
                            style={{ width: '15%' }}
                            title={String(stock.quantity ?? '')}
                            data-testid="stock-row-qty"
                          >
                            {stock.quantity}
                          </td>
                          <td
                            style={{ width: '15%' }}
                            title={formatStockDate(stock.lastImportId)}
                            data-testid="stock-row-update-date"
                          >
                            {formatStockDate(stock.lastImportId)}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={6} className="py-4! text-center">
                          {stocks.isFetchingNextPage && (
                            <div className="text-muted">
                              <FontAwesomeIcon icon={faCircleNotch} spin />{' '}
                              Loading more items...
                            </div>
                          )}
                          {nextCursor ? (
                            <button
                              type="button"
                              className="btn btn-link shadow-none"
                              onClick={() => void stocks.fetchNextPage()}
                              data-testid="stock-load-more-button"
                            >
                              Load more items...
                            </button>
                          ) : (
                            <span className="text-muted">No more items</span>
                          )}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
